/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Client } from '@app/clients/clients/client.model';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ClientsService } from '@app/clients/clients/clients.service';
import { ApiQuery } from '@app/core/http/api-query';
import { ContactComponent } from '@app/shared/components/contact/contact.component';
import { ConfirmService } from '@app/shared/services/confirm.service';
import { forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ContactPersonComponent } from '@app/shared/components/contact-person/contact-person.component';
import { BankAccountsComponent } from '@app/shared/components/bank-accounts/bank-accounts.component';
import { ClientsTypeEnum } from '@app/clients/clients/clients-type-enum.enum';
import { LaravelModelsEnum } from '@app/shared/models/model.model';
import { Logger } from '@app/core';

const log = new Logger('ClientsFormComponent');

@Component({
  selector: 'app-clients-form',
  templateUrl: './clients-form.component.html',
  styleUrls: ['./clients-form.component.scss'],
})
export class ClientsFormComponent implements OnInit {
  @Input() client: Client = null;
  form: FormGroup;
  clientTypeEnum = ClientsTypeEnum;
  LaravelModelsEnum = LaravelModelsEnum;

  @ViewChild(ContactComponent) contactComponent: ContactComponent;
  @ViewChild(ContactPersonComponent) contactPersonsComponent: ContactPersonComponent;

  constructor(public modal: NgbActiveModal, private formBuilder: FormBuilder, private clientService: ClientsService, private confirmService: ConfirmService) {}

  ngOnInit() {
    this.createForm();
    this.patchForm();
  }

  save() {
    // First, check contacts for duplicates (only unchecked ones will be queried by the child)
    const checks = [];
    if (this.contactComponent) checks.push(this.contactComponent.checkAllContactsExists());
    if (this.contactPersonsComponent) checks.push(this.contactPersonsComponent.checkAllContactsExists());

    if (checks.length > 0) {
      forkJoin(checks)
        .pipe(map((results) => results.some((r) => !!r)))
        .subscribe((hasDuplicates) => {
          if (hasDuplicates) {
            this.confirmService.confirm('Duplicates found. Save anyway?', 'Duplicate contacts').subscribe((ok) => {
              if (ok) this.performSave();
            });
          } else {
            this.performSave();
          }
        });
    } else {
      this.performSave();
    }
  }

  private performSave() {
    if (this.client && this.client.id) {
      const client: Client = new Client().deserialize(this.formatPost());
      this.clientService.update(client).subscribe((res) => this.modal.close(res));
    } else {
      const event: Client = new Client().deserialize(this.formatPost());
      this.clientService.store(event).subscribe((res) => this.modal.close(res));
    }
  }

  log() {
    log.debug(this.form);
  }

  private formatPost() {
    const formatedJson = this.form.value;
    if (formatedJson.type_id !== this.clientTypeEnum.company) {
      formatedJson.contact_persons = [];
    }
    return formatedJson;
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      id: [null],
      name: [null, [Validators.required]],
      description: [null],
      address: [null],
      vat_number: [null],
      responsible_person: [''],
      type_id: [null, [Validators.required]],
      city_id: [null],
      city: [null],
      country_id: [null, [Validators.required]],
      country: [null],
      locales: [[]],
      industry_id: [null],
      industry: [null],
      contacts: new FormArray([]),
      contact_persons: new FormArray([]),
      bank_accounts: new FormArray([]),
      active: [true],
      tags: [],
    });
  }

  private patchForm() {
    /*
     * гетва client-а и го пълни
     */
    if (this.client && this.client.id) {
      /*
       * Гетва client-a от сървиса по ID
       * Тук се подават като параметри всички includes(relations), които са нужни
       */
      const apiQuery: ApiQuery = new ApiQuery()
        .setLimit(0)
        .addIncludes(
          'bank_accounts',
          'city.country',
          'country',
          'locales',
          'contacts',
          'industry',
          'contact_persons.contacts',
          'contact_persons.event_group',
          'tags'
        );
      this.clientService.show(this.client.id, apiQuery).subscribe((client: Client) => {
        /*
         *  Обикаля се взетия от сървиса client и се нагласят някои неща, за да може да се patch-не
         */
        if (client['contacts'].length > 0) {
          this.form.setControl('contacts', ContactComponent.createMany(client['contacts']));
        }

        if (client['contact_persons'].length > 0) {
          this.form.setControl('contact_persons', ContactPersonComponent.createMany(client['contact_persons'], true));
        }

        if (client['bank_accounts'].length > 0) {
          this.form.setControl('bank_accounts', BankAccountsComponent.createMany(client['bank_accounts']));
        }

        setTimeout(() => {
          this.form.patchValue(client);

          // /*
          //  * Tова трябва да се случи след patch-ването,
          //  * защото пълни locale id-та в един масив като стойност на един FormControl,
          //  * а не в различни както е в стандартния случай.
          //  * Компонента(locales-select) се грижи за попълването
          //  */
          // client.locales.forEach((locale: Locale) => {
          //   this.form.get('locales').value.push(locale);
          // });

          this.form.updateValueAndValidity();
        });
      });
    } else if (this.client) {
      this.form.patchValue(this.client);
    }
  }
}
